import type { ArtistFullProfileInfoDto, ArtistItem } from '@customTypes/types';
import {
  AppShell,
  Button,
  Card,
  Flex,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Title,
  Badge,
  Progress
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useState } from 'react';

import { useGetArtistsActivityListQuery } from '@/features/admin-panel/api/getArtistsActivitiList/hook';
import { useArtistAllWorkItemsQuery } from '@/features/work/api/artist-all-work-items-list';
import { useShowFullArtistProfileQuery } from '@/features/work/api/show-full-artist-profile';
import { ArtistFullProfile } from '@/features/work/view/artist-full-profile/ArtistFullProfile';
import { useArtistProfileProgressQuery } from '@/shared/api/requests/profile-progress/artist';

type ArtistRowProps = {
  artist: ArtistItem;
  descendingIndex: number;
  openProfile: () => void;
  openContractsModal: () => void;
  setCurrentProfileId: (id: string) => void;
  setCurrentArtist: (id: number) => void;
};

const ArtistRow = ({
  artist,
  descendingIndex,
  openProfile,
  openContractsModal,
  setCurrentProfileId,
  setCurrentArtist
}: ArtistRowProps) => {
  const { data: fullProfile, isLoading: isStatusLoading } =
    useShowFullArtistProfileQuery({
      profileId: artist.artistProfileId.toString()
    });
  const { data: progressData, isLoading: isProgressLoading } =
    useArtistProfileProgressQuery({
      profileId: artist.artistProfileId.toString()
    });
  const status = fullProfile?.data?.status;
  const progress = progressData?.data?.totalCompletion;

  const ARTIST_STATUS_COLORS: Record<string, string> = {
    LookingForWork: 'green',
    OnContract: 'blue',
    FinishingContract: 'yellow',
    OnVacation: 'orange',
    HiddenFromSearch: 'gray'
  };

  const ARTIST_STATUS_LABELS: Record<string, string> = {
    LookingForWork: 'Поиск',
    OnContract: 'Контракт',
    FinishingContract: 'Финиш',
    OnVacation: 'Отпуск',
    HiddenFromSearch: 'Скрыт'
  };

  return (
    <Table.Tr key={artist.artistProfileId}>
      <Table.Td>{descendingIndex}</Table.Td>
      <Table.Td>{artist.artistProfileId}</Table.Td>
      <Table.Td>{artist.firstName ?? '-'}</Table.Td>
      <Table.Td>{artist.lastName ?? '-'}</Table.Td>
      <Table.Td>{artist.email ?? '-'}</Table.Td>
      <Table.Td>{artist.referrerEmail ?? '-'}</Table.Td>
      <Table.Td>
        {isStatusLoading ? (
          <Loader size='xs' />
        ) : status ? (
          <Badge
            color={ARTIST_STATUS_COLORS[status] || 'gray'}
            variant='filled'
          >
            {ARTIST_STATUS_LABELS[status] || status}
          </Badge>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        {isProgressLoading ? (
          <Loader size='xs' />
        ) : typeof progress === 'number' ? (
          <Flex direction='column' gap={4}>
            <Text size='xs'>{progress}%</Text>
            <Progress radius='xl' size='sm' value={progress} />
          </Flex>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        <Flex gap='xs'>
          <Button
            size='xs'
            variant='filled'
            onClick={() => {
              setCurrentProfileId(artist.artistProfileId.toString());
              openProfile();
            }}
          >
            Профиль
          </Button>
          <Button
            size='xs'
            variant='outline'
            onClick={() => {
              setCurrentArtist(artist.artistProfileId);
              openContractsModal();
            }}
          >
            Контракты
          </Button>
        </Flex>
      </Table.Td>
    </Table.Tr>
  );
};

export const Referrers = () => {
  const [artistId, setArtistId] = useState('');
  const [artistsPage, setArtistsPage] = useState(1);
  const [isArtistsListOpen, { open: openArtistsList }] = useDisclosure();
  const [contractsModalOpened, contractsModal] = useDisclosure();
  const [currentArtist, setCurrentArtist] = useState<number | null>(null);
  const [isProfileOpen, { open: openProfile, close: closeProfile }] =
    useDisclosure();
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);

  const profileQuery = useShowFullArtistProfileQuery(
    { profileId: artistId },
    { options: { enabled: false } }
  );

  const onSearch = () => {
    if (artistId.trim()) {
      profileQuery.refetch();
    }
  };

  type ExtendedProfile = ArtistFullProfileInfoDto & {
    email?: string;
    loyaltyPoints?: number;
  };

  const data: ExtendedProfile | undefined = profileQuery.data?.data;

  const artistsQuery = useGetArtistsActivityListQuery(
    {
      pageNumber: artistsPage.toString(),
      referrerEmail: data?.email
    },
    {
      options: { enabled: isArtistsListOpen && !!data?.email }
    }
  );

  const contractsQuery = useArtistAllWorkItemsQuery(
    { profileId: currentArtist?.toString() ?? '' },
    { options: { enabled: contractsModalOpened && !!currentArtist } }
  );

  return (
    <AppShell.Main>
      <Modal
        withCloseButton
        opened={isProfileOpen}
        overlayProps={{ blur: 8, opacity: 0.6 }}
        onClose={closeProfile}
      >
        {currentProfileId && (
          <ScrollArea p='md'>
            <ArtistFullProfile artistProfileId={currentProfileId} />
          </ScrollArea>
        )}
      </Modal>
      <Modal
        withCloseButton
        opened={contractsModalOpened}
        overlayProps={{ blur: 4, opacity: 0.6 }}
        size='80%'
        title='Контракты'
        onClose={contractsModal.close}
      >
        {contractsQuery.isFetching && <Loader />}
        {contractsQuery.isError && (
          <Text c='red'>Не удалось загрузить список контрактов</Text>
        )}
        {contractsQuery.data && contractsQuery.data.data.length === 0 && (
          <Text>Контрактов пока нет</Text>
        )}
        {contractsQuery.data && contractsQuery.data.data.length > 0 && (
          <ScrollArea h={600}>
            <Table highlightOnHover striped withColumnBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Тип</Table.Th>
                  <Table.Th>Артист</Table.Th>
                  <Table.Th>Клуб</Table.Th>
                  <Table.Th>Начало</Table.Th>
                  <Table.Th>Конец</Table.Th>
                  <Table.Th>Создано</Table.Th>
                  <Table.Th>Обновлено</Table.Th>
                  <Table.Th>Статус</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {contractsQuery.data.data.map((item) => (
                  <Table.Tr key={item.workItemId}>
                    <Table.Td>{item.workItemId}</Table.Td>
                    <Table.Td>{item.workItemType}</Table.Td>
                    <Table.Td>{item.artistName ?? '-'}</Table.Td>
                    <Table.Td>{item.clubName ?? '-'}</Table.Td>
                    <Table.Td>
                      {dayjs(item.startDate).format('DD.MM.YYYY')}
                    </Table.Td>
                    <Table.Td>
                      {item.endDate
                        ? dayjs(item.endDate).format('DD.MM.YYYY')
                        : '-'}
                    </Table.Td>
                    <Table.Td>
                      {dayjs(item.createdAt).format('DD.MM.YYYY HH:mm')}
                    </Table.Td>
                    <Table.Td>
                      {dayjs(item.updatedAt).format('DD.MM.YYYY HH:mm')}
                    </Table.Td>
                    <Table.Td>{item.status}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Modal>
      <Flex align='center' justify='space-between' mb='lg'>
        <Title order={2}>Рефереры</Title>
      </Flex>

      <Card withBorder mb='lg' p='lg' radius='md' shadow='sm'>
        <Flex align='end' gap='sm' wrap='wrap'>
          <TextInput
            label={<Text fw={500}>ID профиля артиста</Text>}
            value={artistId}
            onChange={(e) => setArtistId(e.currentTarget.value)}
          />
          <Button onClick={onSearch}>Найти</Button>
        </Flex>
      </Card>

      {profileQuery.isFetching && <Loader />}
      {profileQuery.isError && (
        <Text c='red' mb='md'>
          Не удалось загрузить данные
        </Text>
      )}

      {data && (
        <Card withBorder p='md' radius='md' shadow='sm'>
          <Table
            withColumnBorders
            withTableBorder
            style={{ tableLayout: 'fixed' }}
          >
            <Table.Tbody>
              <Table.Tr>
                <Table.Th>Имя</Table.Th>
                <Table.Td>{data.info?.firstName ?? '-'}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Фамилия</Table.Th>
                <Table.Td>{data.info?.lastName ?? '-'}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Email</Table.Th>
                <Table.Td>{data.email ?? '-'}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>К-во человек прошли по ссылке</Table.Th>
                <Table.Td>{data.loyaltyPoints ?? '-'}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          <Button mt='md' onClick={openArtistsList}>
            Посмотреть артистов
          </Button>
        </Card>
      )}

      {isArtistsListOpen && (
        <Card withBorder mt='lg' p='md' radius='md'>
          <Text fw={500} mb='sm' size='lg'>
            Артисты
          </Text>
          {artistsQuery.isFetching && <Loader />}
          {artistsQuery.isError && (
            <Text c='red' mb='md'>
              Не удалось загрузить список артистов
            </Text>
          )}
          {artistsQuery.data && (
            <>
              <ScrollArea h={300}>
                <Table
                  highlightOnHover
                  striped
                  withColumnBorders
                  withTableBorder
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>No</Table.Th>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Имя</Table.Th>
                      <Table.Th>Фамилия</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Кто пригласил</Table.Th>
                      <Table.Th>Статус профиля</Table.Th>
                      <Table.Th>Прогресс профиля</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {artistsQuery.data.data.items.map((artist, index) => {
                      const pageSize = artistsQuery.data?.data.pageSize ?? 10;
                      const totalCount =
                        artistsQuery.data?.data.totalCount ?? 0;
                      const descendingIndex =
                        totalCount - ((artistsPage - 1) * pageSize + index);
                      return (
                        <ArtistRow
                          key={artist.artistProfileId}
                          artist={artist}
                          descendingIndex={descendingIndex}
                          openContractsModal={contractsModal.open}
                          openProfile={openProfile}
                          setCurrentArtist={setCurrentArtist}
                          setCurrentProfileId={setCurrentProfileId}
                        />
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <Pagination
                boundaries={1}
                mt='md'
                siblings={1}
                total={artistsQuery.data.data.totalPages}
                value={artistsPage}
                onChange={setArtistsPage}
              />
            </>
          )}
        </Card>
      )}
    </AppShell.Main>
  );
};
